// React Query hooks for products. Thin wrappers over lib/api/products.ts.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  setProductsActive,
  hardDeleteUnusedProducts,
  hardDeleteProduct,
  bulkCreateProducts,
  type CreateProductInput,
  type UpdateProductInput,
  type DeleteProductInput,
  type BulkCreateProductsInput,
  upsertProducts,
} from '@/lib/api/products';

const KEYS = {
  all: ['products'] as const,
  list: (filters?: object) => [...KEYS.all, 'list', filters ?? {}] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export const useProducts = (filters?: { isActive?: boolean; categoryId?: string }) =>
  useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => listProducts(filters),
  });

export const useProduct = (id: string | null) =>
  useQuery({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: () => (id ? getProduct(id) : Promise.resolve(null)),
    enabled: !!id,
  });

export const useCreateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => createProduct(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useUpdateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProductInput }) =>
      updateProduct(id, input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
};

export const useDeleteProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DeleteProductInput) => deleteProduct(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

/** Permanent delete; the API refuses if the product was ever used. */
export const useHardDeleteProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hardDeleteProduct(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

/**
 * Bulk create — used by the CSV import dialog. Single batched write
 * (up to 400 products), then invalidates the product lists.
 */
export const useBulkCreateProducts = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkCreateProductsInput) => bulkCreateProducts(input),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      // Touch the specific lists so the page refreshes the moment the
      // mutation completes (without waiting for a manual refresh).
      qc.invalidateQueries({ queryKey: KEYS.list({ isActive: true }) });
      qc.invalidateQueries({ queryKey: KEYS.list({}) });
      return result;
    },
  });
};

/**
 * Bulk create-or-update by SKU. Invalidates the whole product tree: an import
 * can touch hundreds of rows across every filter and cached list.
 */
export const useUpsertProducts = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: Parameters<typeof upsertProducts>[0]) => upsertProducts(items),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

/**
 * Bulk deactivate or reactivate the checkbox selection on the Inventory page.
 * Invalidates the whole product tree: the rows it touches can sit in any
 * cached list or filter. On settle rather than success -- the writes go out in
 * chunks, so a failure can arrive after some of them already landed.
 */
export const useSetProductsActive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      setProductsActive(ids, isActive),
    onSettled: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

/** Bulk permanent delete of whatever part of the Inventory selection nothing references. */
export const useHardDeleteUnusedProducts = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => hardDeleteUnusedProducts(ids),
    // Settled, for the same reason as above: the chunks delete independently.
    onSettled: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};
